import { computed, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue'
import {
  isMobileWorkspace,
  resolvePortraitLayoutViewport,
  WORKSPACE_TABLET_MAX_SHORT_SIDE,
} from '@/platform/viewport/layoutViewport'

const MIN_SCENE_PANE_WIDTH = 136
const MAX_SCENE_PANE_WIDTH = 360
const EVENT_PALETTE_TEXT_WIDTH = 110
const EVENT_PROP_LABEL_WIDTH = 72
const EVENT_PROP_ROW_HORIZONTAL_PADDING = 20
const EVENT_PROP_ROW_COLUMN_GAP = 10
/** 显示文本「角色名」输入框在组合行中的最小宽度。 */
const ROLE_NAME_FIELD_MIN_WIDTH = 88
/** 显示文本「角色列表」下拉框固定宽度（与 EventByCode100 一致）。 */
const ROLE_CACHE_SELECT_WIDTH = 96
const ROLE_COMBO_INNER_GAP = 8
/** 事件属性区至少容纳属性名 + 角色名/角色列表组合行。 */
const MIN_EVENT_PROPS_PANE_WIDTH =
  EVENT_PROP_LABEL_WIDTH
  + EVENT_PROP_ROW_COLUMN_GAP
  + EVENT_PROP_ROW_HORIZONTAL_PADDING
  + ROLE_NAME_FIELD_MIN_WIDTH
  + ROLE_COMBO_INNER_GAP
  + ROLE_CACHE_SELECT_WIDTH
/** 右栏 = 事件按钮栏（文字模式）+ 事件列表/属性区，需保证属性区满足上一约束。 */
const MIN_RIGHT_PANE_WIDTH = MIN_EVENT_PROPS_PANE_WIDTH + EVENT_PALETTE_TEXT_WIDTH
const MAX_RIGHT_PANE_WIDTH = 640
const MIN_CENTER_PANE_WIDTH = 280
const MIN_PREVIEW_HEIGHT = 260
const MIN_RESOURCE_HEIGHT_DESKTOP = 220
const MIN_RESOURCE_HEIGHT_TOUCH = 260
const MIN_RESOURCE_HEIGHT_RATIO = 1 / 3
const MAX_PREVIEW_RATIO = 0.9
const MIN_EVENT_LIST_RATIO = 0.3
const MAX_EVENT_LIST_RATIO = 0.8
const PORTRAIT_LAYOUT_MAX_WIDTH = WORKSPACE_TABLET_MAX_SHORT_SIDE
const PORTRAIT_MIN_STORY_WIDTH = 88
const PORTRAIT_MIN_PROPS_RATIO = 0.25
const PORTRAIT_MAX_PROPS_RATIO = 0.85
const PORTRAIT_DEFAULT_PROPS_RATIO = 0.5
const PORTRAIT_HANDLE_DRAG_THRESHOLD = 4

export type PortraitMainView = 'events' | 'resources'

/** 将数值限制在 [min, max] 内，供分栏拖拽使用。 */
function clampValue(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * 工程编辑器布局：PC/横屏平板三栏分栏 + 竖屏独立模板。
 *
 * **【竖屏独立】** `isPortraitLayout` 为 true 时使用 `editor-body-portrait`：
 * 预览在上、剧情/资源 Tab、左右浮层把手，无 scene/right 分割条。
 *
 * **【横屏平板】/【PC 独立】** 同一套三栏 DOM：`scenePaneWidth`、`rightPaneWidth`、
 * 预览/资源、事件列表/属性比例拖拽；触屏用更大最小资源区高度。
 *
 * 布局判定统一来自 `resolvePortraitLayoutViewport()`，禁止在本模块写 1180 魔法数。
 */
export function useProjectViewerLayout(options: {
  previewAspectRatio: Ref<number>
}) {
  const editorBodyRef = ref<HTMLElement | null>(null)
  const rightMainRef = ref<HTMLElement | null>(null)
  const centerPaneRef = ref<HTMLElement | null>(null)

  const scenePaneWidth = ref(196)
  const rightPaneWidth = ref(MIN_RIGHT_PANE_WIDTH)
  const previewPaneRatio = ref(0.68)
  const previewPaneHeight = ref<number | null>(null)
  const eventListPaneRatio = ref(0.58)
  const isScenePaneCollapsed = ref(false)
  const isResourcePaneCollapsed = ref(false)
  const isPortraitLayout = ref(false)
  const hasManualPreviewRatio = ref(false)
  const portraitMainView = ref<PortraitMainView>('events')
  const portraitStoryOpen = ref(false)
  const portraitPropsOpen = ref(false)
  const portraitStoryWidth = ref(EVENT_PALETTE_TEXT_WIDTH)
  const portraitPropsWidthRatio = ref(PORTRAIT_DEFAULT_PROPS_RATIO)
  const portraitMainContentRef = ref<HTMLElement | null>(null)
  const portraitEventsWorkspaceRef = ref<HTMLElement | null>(null)

  const editorBodyVars = computed(
    () => {
      const sceneWidth = isScenePaneCollapsed.value ? 0 : scenePaneWidth.value
      const previewRatio = isResourcePaneCollapsed.value ? 1 : previewPaneRatio.value
      const resourceRatio = isResourcePaneCollapsed.value ? 0 : 1 - previewPaneRatio.value
      const previewBasis = isResourcePaneCollapsed.value
        ? '100%'
        : previewPaneHeight.value == null
          ? `${previewRatio * 100}%`
          : `${previewPaneHeight.value}px`
      const resourceMin = isResourcePaneCollapsed.value ? 0 : getMinResourceHeight(centerPaneRef.value?.clientHeight ?? 0)
      const aspect = Math.max(0.1, Number(options.previewAspectRatio.value) || (16 / 9))
      return `--pv-scene-w:${sceneWidth}px;--pv-right-w:${rightPaneWidth.value}px;--pv-right-min-w:${MIN_RIGHT_PANE_WIDTH}px;--pv-preview:${previewRatio};--pv-preview-basis:${previewBasis};--pv-resource:${resourceRatio};--pv-resource-min:${resourceMin}px;--pv-el:${eventListPaneRatio.value};--pv-ep:${1 - eventListPaneRatio.value};--pv-preview-aspect:${aspect};--pv-portrait-props-ratio:${portraitPropsWidthRatio.value};--pv-portrait-story-w:${portraitStoryWidth.value}px;--pv-portrait-palette-w:${EVENT_PALETTE_TEXT_WIDTH}px;`
    },
  )

  let stopDragging: (() => void) | null = null
  let centerPaneResizeObserver: ResizeObserver | null = null

  function isEditorBodyColumnLayout(): boolean {
    const body = editorBodyRef.value
    if (!body) return false
    return window.getComputedStyle(body).flexDirection === 'column'
  }

  function isTouchLayout(): boolean {
    return isMobileWorkspace(PORTRAIT_LAYOUT_MAX_WIDTH)
  }

  function updatePortraitLayoutState(): void {
    if (typeof window === 'undefined') return
    isPortraitLayout.value = resolvePortraitLayoutViewport(
      PORTRAIT_LAYOUT_MAX_WIDTH,
      isPortraitLayout.value,
    )
    if (isPortraitLayout.value) {
      clampPortraitStoryWidth()
    }
  }

  function getMinResourceHeight(totalHeight = 0): number {
    const minByDevice = isTouchLayout() ? MIN_RESOURCE_HEIGHT_TOUCH : MIN_RESOURCE_HEIGHT_DESKTOP
    const minByRatio = totalHeight > 0 ? Math.floor(totalHeight * MIN_RESOURCE_HEIGHT_RATIO) : 0
    return Math.max(minByDevice, minByRatio)
  }

  function clampPreviewRatioByHeight(ratio: number, totalHeight: number): number {
    const minResourceHeight = getMinResourceHeight(totalHeight)
    const maxByResource = totalHeight > 0
      ? Math.max(0.35, (totalHeight - minResourceHeight) / totalHeight)
      : MAX_PREVIEW_RATIO
    return clampValue(ratio, 0.35, Math.min(MAX_PREVIEW_RATIO, maxByResource))
  }

  function syncAutoPreviewRatio(): void {
    if (hasManualPreviewRatio.value || isResourcePaneCollapsed.value) return
    const pane = centerPaneRef.value
    if (!pane) return
    const paneWidth = pane.clientWidth
    const paneHeight = pane.clientHeight
    if (paneWidth <= 0 || paneHeight <= 0) return
    const aspect = Number(options.previewAspectRatio.value) || (16 / 9)
    const previewChromeHeight = isTouchLayout() ? 22 : 30
    const desiredPreviewHeight = Math.ceil((paneWidth / Math.max(aspect, 0.1)) + previewChromeHeight)
    const previewHeight = Math.max(MIN_PREVIEW_HEIGHT, Math.min(desiredPreviewHeight, paneHeight - getMinResourceHeight(paneHeight)))
    const nextRatio = clampPreviewRatioByHeight(previewHeight / paneHeight, paneHeight)
    previewPaneRatio.value = nextRatio
    previewPaneHeight.value = Math.round(nextRatio * paneHeight)
  }

  /**
   * 注册一次拖拽监听，并在拖拽结束后释放全局事件。
   *
   * @param event 用户按下分隔条时的指针事件。
   * @param onMove 位移回调，接收 X/Y 两个方向的偏移量。
   * @returns 无返回值。
   */
  function startDragging(
    event: PointerEvent,
    onMove: (deltaX: number, deltaY: number) => void,
  ): void {
    stopDragging?.()
    const startX = event.clientX
    const startY = event.clientY

    const handleMove = (nextEvent: PointerEvent) => {
      onMove(nextEvent.clientX - startX, nextEvent.clientY - startY)
    }

    const cleanup = () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', cleanup)
      window.removeEventListener('pointercancel', cleanup)
      document.body.classList.remove('pane-resizing')
      stopDragging = null
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', cleanup)
    window.addEventListener('pointercancel', cleanup)
    document.body.classList.add('pane-resizing')
    stopDragging = cleanup
  }

  /**
   * 调整左侧场景树面板宽度。
   *
   * @param event 分隔条的指针按下事件。
   * @returns 无返回值。
   */
  function dragSceneWidth(event: PointerEvent): void {
    event.preventDefault()
    if (isPortraitLayout.value) return
    if (isScenePaneCollapsed.value || isEditorBodyColumnLayout()) return
    const startWidth = scenePaneWidth.value
    startDragging(event, (deltaX) => {
      const bodyWidth = editorBodyRef.value?.clientWidth ?? window.innerWidth
      const maxWidth = Math.min(
        MAX_SCENE_PANE_WIDTH,
        Math.max(MIN_SCENE_PANE_WIDTH, bodyWidth - rightPaneWidth.value - MIN_CENTER_PANE_WIDTH),
      )
      scenePaneWidth.value = clampValue(startWidth + deltaX, MIN_SCENE_PANE_WIDTH, maxWidth)
    })
  }

  /**
   * 调整右侧事件面板宽度。
   *
   * @param event 分隔条的指针按下事件。
   * @returns 无返回值。
   */
  function dragRightWidth(event: PointerEvent): void {
    event.preventDefault()
    if (isPortraitLayout.value) return
    if (isEditorBodyColumnLayout()) return
    const startWidth = rightPaneWidth.value
    startDragging(event, (deltaX) => {
      const bodyWidth = editorBodyRef.value?.clientWidth ?? window.innerWidth
      const maxWidth = Math.min(
        MAX_RIGHT_PANE_WIDTH,
        Math.max(MIN_RIGHT_PANE_WIDTH, bodyWidth - scenePaneWidth.value - MIN_CENTER_PANE_WIDTH),
      )
      rightPaneWidth.value = clampValue(startWidth - deltaX, MIN_RIGHT_PANE_WIDTH, maxWidth)
    })
  }

  /**
   * 调整中间区域预览区与资源区的高度占比。
   *
   * @param event 分隔条的指针按下事件。
   * @returns 无返回值。
   */
  function dragPreviewHeight(event: PointerEvent): void {
    event.preventDefault()
    if (isPortraitLayout.value) return
    if (isResourcePaneCollapsed.value) return
    hasManualPreviewRatio.value = true
    const startRatio = previewPaneRatio.value
    const totalHeight = centerPaneRef.value?.clientHeight ?? 1
    startDragging(event, (_, deltaY) => {
      previewPaneRatio.value = clampPreviewRatioByHeight(
        startRatio + deltaY / totalHeight,
        totalHeight,
      )
      previewPaneHeight.value = Math.round(previewPaneRatio.value * totalHeight)
    })
  }

  /**
   * 调整右侧事件列表与属性面板的高度占比。
   *
   * @param event 分隔条的指针按下事件。
   * @returns 无返回值。
   */
  function dragEventHeight(event: PointerEvent): void {
    event.preventDefault()
    if (isPortraitLayout.value) return
    const startRatio = eventListPaneRatio.value
    const totalHeight = rightMainRef.value?.clientHeight ?? 1
    startDragging(event, (_, deltaY) => {
      eventListPaneRatio.value = clampValue(
        startRatio + deltaY / totalHeight,
        MIN_EVENT_LIST_RATIO,
        MAX_EVENT_LIST_RATIO,
      )
    })
  }

  /**
   * 主动清理拖拽监听，避免组件卸载后残留全局事件。
   *
   * @returns 无返回值。
   */
  function cleanupDragging(): void {
    stopDragging?.()
  }

  /** 切换左侧剧情树面板折叠状态。 */
  function toggleScenePane(): void {
    isScenePaneCollapsed.value = !isScenePaneCollapsed.value
  }

  /** 切换底部资源面板折叠状态。 */
  function toggleResourcePane(): void {
    isResourcePaneCollapsed.value = !isResourcePaneCollapsed.value
  }

  /** 竖屏：切换剧情树 / 属性浮层显隐（资源视图下无效）。 */
  function togglePortraitPane(pane: 'story' | 'props'): void {
    if (portraitMainView.value === 'resources') return
    if (pane === 'story') {
      portraitStoryOpen.value = !portraitStoryOpen.value
      return
    }
    portraitPropsOpen.value = !portraitPropsOpen.value
  }

  /** 竖屏：切换剧情树浮层显隐（资源视图下无效）。 */
  function togglePortraitStoryPane(): void {
    togglePortraitPane('story')
  }

  /** 竖屏：切换主区「剧情 / 资源」视图；浮层展开状态在切换后保持不变。 */
  function togglePortraitResourceView(): void {
    if (portraitMainView.value === 'resources') {
      portraitMainView.value = 'events'
      return
    }
    portraitMainView.value = 'resources'
  }

  /** 竖屏：设置主区视图；剧情树 / 属性浮层展开状态不因切换主视图而重置。 */
  function setPortraitMainView(view: PortraitMainView): void {
    portraitMainView.value = view
  }

  /**
   * 区分竖屏浮层把手的点击与水平拖拽：小幅移动视为切换，超出阈值则进入调宽。
   *
   * @param event 把手 pointerdown。
   * @param options.onToggle 点击收起/展开。
   * @param options.onDrag 拖拽调宽（传入实际开始拖拽时的 pointer 事件）。
   */
  function bindPortraitHandleInteraction(
    event: PointerEvent,
    options: {
      onToggle: () => void
      onDrag: (event: PointerEvent) => void
    },
  ): void {
    const startX = event.clientX
    const startY = event.clientY
    let dragging = false

    const handleMove = (moveEvent: PointerEvent) => {
      if (dragging) return
      const deltaX = Math.abs(moveEvent.clientX - startX)
      const deltaY = Math.abs(moveEvent.clientY - startY)
      if (deltaX <= PORTRAIT_HANDLE_DRAG_THRESHOLD && deltaY <= PORTRAIT_HANDLE_DRAG_THRESHOLD) return
      dragging = true
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
      options.onDrag(moveEvent)
    }

    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
      if (!dragging) {
        options.onToggle()
      }
    }

    event.preventDefault()
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)
  }

  /**
   * 剧情树浮层最大宽度为事件工作区全宽，默认宽度为事件按钮栏列宽。
   *
   * @returns 剧情树浮层允许的最大像素宽度。
   */
  function getPortraitStoryMaxWidth(): number {
    const workspaceWidth = portraitEventsWorkspaceRef.value?.clientWidth ?? 0
    return workspaceWidth > 0 ? workspaceWidth : EVENT_PALETTE_TEXT_WIDTH
  }

  /**
   * 将剧情树浮层宽度约束在按钮栏列宽与事件工作区全宽之间。
   */
  function clampPortraitStoryWidth(): void {
    portraitStoryWidth.value = clampValue(
      portraitStoryWidth.value,
      PORTRAIT_MIN_STORY_WIDTH,
      getPortraitStoryMaxWidth(),
    )
  }

  /**
   * 竖屏：拖拽调节剧情树浮层宽度（像素，最大为事件工作区全宽）。
   *
   * @param event 把手 pointer 事件。
   */
  function dragPortraitStoryWidth(event: PointerEvent): void {
    event.preventDefault()
    if (!isPortraitLayout.value) return
    clampPortraitStoryWidth()
    const startWidth = portraitStoryWidth.value
    startDragging(event, (deltaX) => {
      portraitStoryWidth.value = clampValue(
        startWidth + deltaX,
        PORTRAIT_MIN_STORY_WIDTH,
        getPortraitStoryMaxWidth(),
      )
    })
  }

  /**
   * 竖屏：拖拽调节属性浮层宽度（相对事件列表区域宽度比例）。
   *
   * @param event 把手 pointer 事件。
   */
  function dragPortraitPropsWidth(event: PointerEvent): void {
    event.preventDefault()
    if (!isPortraitLayout.value) return
    const contentWidth = portraitMainContentRef.value?.clientWidth ?? 0
    if (contentWidth <= 0) return
    const startRatio = portraitPropsWidthRatio.value
    startDragging(event, (deltaX) => {
      portraitPropsWidthRatio.value = clampValue(
        startRatio - deltaX / contentWidth,
        PORTRAIT_MIN_PROPS_RATIO,
        PORTRAIT_MAX_PROPS_RATIO,
      )
    })
  }

  /**
   * 竖屏：浮层把手 pointerdown（点击切换 / 拖拽调宽）。
   *
   * @param pane 剧情树或属性浮层。
   */
  function onPortraitHandlePointerDown(pane: 'story' | 'props', event: PointerEvent): void {
    if (portraitMainView.value === 'resources') return
    bindPortraitHandleInteraction(event, {
      onToggle: () => togglePortraitPane(pane),
      onDrag: (dragEvent) => {
        if (pane === 'story') {
          if (!portraitStoryOpen.value) portraitStoryOpen.value = true
          dragPortraitStoryWidth(dragEvent)
          return
        }
        if (!portraitPropsOpen.value) portraitPropsOpen.value = true
        dragPortraitPropsWidth(dragEvent)
      },
    })
  }

  function handleViewportResize(): void {
    updatePortraitLayoutState()
    syncAutoPreviewRatio()
  }

  onMounted(() => {
    updatePortraitLayoutState()
    if (typeof ResizeObserver !== 'undefined' && centerPaneRef.value) {
      centerPaneResizeObserver = new ResizeObserver(() => syncAutoPreviewRatio())
      centerPaneResizeObserver.observe(centerPaneRef.value)
    }
    window.addEventListener('resize', handleViewportResize, { passive: true })
    window.addEventListener('focusin', handleViewportResize, { passive: true })
    window.visualViewport?.addEventListener('resize', handleViewportResize, { passive: true })
    window.visualViewport?.addEventListener('scroll', handleViewportResize, { passive: true })
    window.screen?.orientation?.addEventListener?.('change', handleViewportResize)
    syncAutoPreviewRatio()
  })

  watch(
    () => [options.previewAspectRatio.value, isResourcePaneCollapsed.value] as const,
    () => syncAutoPreviewRatio(),
  )

  onBeforeUnmount(() => {
    cleanupDragging()
    centerPaneResizeObserver?.disconnect()
    centerPaneResizeObserver = null
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', handleViewportResize)
      window.removeEventListener('focusin', handleViewportResize)
      window.visualViewport?.removeEventListener('resize', handleViewportResize)
      window.visualViewport?.removeEventListener('scroll', handleViewportResize)
      window.screen?.orientation?.removeEventListener?.('change', handleViewportResize)
    }
  })

  return {
    centerPaneRef,
    cleanupDragging,
    dragEventHeight,
    dragPreviewHeight,
    dragRightWidth,
    dragSceneWidth,
    editorBodyRef,
    editorBodyVars,
    isPortraitLayout,
    isResourcePaneCollapsed,
    isScenePaneCollapsed,
    onPortraitHandlePointerDown,
    portraitEventsWorkspaceRef,
    portraitMainContentRef,
    portraitMainView,
    portraitPropsOpen,
    portraitStoryOpen,
    rightMainRef,
    setPortraitMainView,
    togglePortraitResourceView,
    togglePortraitStoryPane,
    toggleResourcePane,
    toggleScenePane,
  }
}
